// Post-related TypeScript interfaces

export interface Post {
  id: string;
  hashCode?: string; // Hash code for SEO-friendly URLs
  userId: string;
  caption: string;
  image?: string;
  video?: string;
  postType: 'regular' | 'poll';
  createdAt: string;
  user?: User;
  comments?: Comment[];
  tags?: Tag[];
  poll?: PostPoll;
}

export interface User {
  id: string;
  username: string;
  name: string;
  image?: string;
  phoneNumber?: string;
  description?: string;
  verified: boolean;
}

export interface Comment {
  id: string;
  postId: string;
  userId: string;
  content: string;
  createdAt: string;
  user?: User;
}

export interface Tag {
  id: string;
  name: string;
  createdAt: string;
}

export interface PostPoll {
  id: string;
  postId: string;
  question: string;
  createdAt: string;
  totalVotes: number;
  userVote?: string;
  options: PostPollOption[];
}

export interface PostPollOption {
  id: string;
  postPollId: string;
  optionText: string;
  optionOrder: number;
  voteCount: number;
  percentage: number;
}

// Utility functions for URL generation - HASH CODE ONLY
export const getPostUrl = (post: Post, baseUrl?: string): string => {
  const base = baseUrl || '';
  
  if (!post.hashCode) {
    throw new Error(`Post ${post.id} is missing hash code. Cannot generate URL without hash code.`);
  }
  
  return `${base}/p/${post.hashCode}`;
};

// For navigation - returns just the path - HASH CODE ONLY
export const getPostPath = (post: Post): string => {
  if (!post.hashCode) {
    throw new Error(`Post ${post.id} is missing hash code. Cannot generate path without hash code.`);
  }
  
  return `/p/${post.hashCode}`;
};

// For cases where you only have ID and hash code separately - HASH CODE REQUIRED
export const getPostPathById = (postId: string, hashCode: string): string => {
  if (!hashCode) {
    throw new Error(`Post ${postId} is missing hash code. Cannot generate path without hash code.`);
  }
  
  return `/p/${hashCode}`;
};

export const getStoryUrl = (storyId: string, hashCode?: string, baseUrl?: string): string => {
  const base = baseUrl || window.location.origin;
  
  if (hashCode) {
    return `${base}/s/${hashCode}`;
  }
  
  // Fallback to ID-based URL
  return `${base}/story/${storyId}`;
};

export const getUserUrl = (userId: string, username?: string, baseUrl?: string): string => {
  const base = baseUrl || window.location.origin;
  
  if (username) {
    return `${base}/@${username}`;
  }
  
  return `${base}/users/${userId}`;
};